import React from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { Filter, FilterType, DEFAULT_SORT_TYPE } from '@model/iceberg';
import { CheckboxFilter } from '@components/filters/v2/filters/checkbox';
import { RadioButtonFilter } from '@components/filters/v2/filters/radio/RadioButtonFilter';
import styled from '@emotion/styled';
import { useI18NextContext } from '@components/hooks';
import { StarRatingFilter } from './filters/stars';
import { ToggleFilter } from '@components/filters/v2/filters/toggle/ToggleFilter';
import { SortBy } from '@components/filters/v2/sort-by/SortBy';
import { performNavigate } from '@state/slices/app';
import { mixins } from '@styles/mixins';
import { PriceFilter } from '@components/filters/v2/filters/price/PriceFilter';
import { TestId } from '@components/test-ids';
import { ThemeProps } from '@theme/base';
import { getQueryAsObject, getQueryAsString } from '@util/common';

export interface FiltersSideBarProps {
  filters: Array<Filter>;
  hasAppliedFilters: boolean;
  onChange: (payload: Array<Filter>) => void;
  onReset: () => void;
  title?: boolean;
  isTours?: boolean;
  isLeftFilter?: boolean;
}

interface TitleProps extends ThemeProps {
  isLeftFilter?: boolean;
}

const Title: any = styled.h5(({ theme, isLeftFilter }: TitleProps) => ({
  ...(theme.custom.typography.h5 as any),
  padding: isLeftFilter ? `2px ${theme.custom.spacing.medium * 2}px 2px 0px` : `2px ${theme.custom.spacing.medium}px`,
  margin: 0
}));

const Reset = styled.a(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  ...mixins(theme).link,
  fontWeight: 500,
  paddingLeft: theme.custom.spacing.xSmall
}));

enum FilterName {
  AIRPORTS = 'departing_airports',
  PROFILE = 'profile',
  MEALBASES = 'mealbases',
  TRIPADVISOR = 'mintripadvisorrating',
  PRICEBAND = 'priceband',
  RATINGS = 'ratings',
  FACILITIES = 'facilities'
}

export const FiltersSideBar = ({
  filters,
  onChange,
  title,
  hasAppliedFilters,
  isTours,
  onReset,
  isLeftFilter
}: FiltersSideBarProps) => {
  const t: any = useI18NextContext();
  const router = useRouter();
  const dispatch = useDispatch();

  const handleOnChange = (payload: Filter) =>
    onChange(filters.map((f: Filter) => (f.name === payload.name ? payload : f)));
  const handleOnReset = () => {
    const { sort } = router.query;
    const routerPath = router.asPath;

    if (hasAppliedFilters && sort && sort !== DEFAULT_SORT_TYPE) {
      const baseUrl = routerPath.split('?')[0];
      const newQuery = {
        ...getQueryAsObject(routerPath),
        sort: DEFAULT_SORT_TYPE
      };
      dispatch(performNavigate(`${baseUrl}?${getQueryAsString(newQuery)}`, undefined, { shallow: true }));
    }
    onReset();
  };

  const renderFilters = () => {
    return filters.map((filter: Filter) => {
      switch (filter.type) {
        case FilterType.SORT:
          return (
            <SortBy
              key={filter.name}
              testId={filter.name}
              filter={filter}
              onChange={handleOnChange}
              isLeftFilter={isLeftFilter}
            />
          );
        case FilterType.CHECKBOX:
          return (
            <CheckboxFilter
              key={filter.name}
              testId={filter.name}
              filter={filter}
              showCount={filter.name !== FilterName.MEALBASES}
              visibleNumberOfFilters={isTours && filter.name === FilterName.AIRPORTS ? Infinity : undefined}
              onChange={handleOnChange}
              isLeftFilter={isLeftFilter}
            />
          );
        case FilterType.STARS:
          return (
            <StarRatingFilter
              key={filter.name}
              testId={filter.name}
              filter={filter}
              onChange={handleOnChange}
              isLeftFilter={isLeftFilter}
            />
          );
        case FilterType.RADIO:
          return (
            <RadioButtonFilter
              key={filter.name}
              testId={filter.name}
              filter={filter}
              onChange={handleOnChange}
              isLeftFilter={isLeftFilter}
            />
          );
        case FilterType.TOGGLE:
          return (
            <ToggleFilter
              key={filter.name}
              testId={filter.name}
              filter={filter}
              onChange={handleOnChange}
              isLeftFilter={isLeftFilter}
            />
          );
        case FilterType.PRICE:
          return <PriceFilter key={filter.name} testId={filter.name} filter={filter} onChange={handleOnChange} />;
        default:
          return <div key={filter.name}>{JSON.stringify(filter)}</div>;
      }
    });
  };
  if (!filters.length) return null;
  return (
    <div data-testid={TestId.dealFinder.filters.container}>
      {title && (
        <Title isLeftFilter={isLeftFilter}>
          {t('filters__toggle-button--label')}
          {hasAppliedFilters && (
            <Reset data-testid={TestId.dealFinder.filters.hasAppliedFilters} onClick={handleOnReset}>
              ({t('common__label--reset').toLowerCase()})
            </Reset>
          )}
        </Title>
      )}
      {renderFilters()}
    </div>
  );
};
